import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dices, RotateCcw, Plus, Minus, Play } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface DiceRoll {
  dice: number[];
  total: number;
  timestamp: string;
}

const DiceRoller: React.FC = () => {
  const [numberOfDice, setNumberOfDice] = useState(2);
  const [diceSides, setDiceSides] = useState(6);
  const [currentRoll, setCurrentRoll] = useState<number[]>([]);
  const [isRolling, setIsRolling] = useState(false);
  const [rollHistory, setRollHistory] = useState<DiceRoll[]>([]);
  const [modifier, setModifier] = useState(0);

  useSEO({
    title: 'Dice Roller - Virtual Dice Rolling Tool',
    description: 'Free online dice roller. Roll multiple dice with customizable sides. Perfect for board games, D&D, and tabletop gaming.',
    keywords: 'dice roller, virtual dice, D&D dice, board game dice, random dice, tabletop gaming',
    canonical: 'https://snap-tools.online/calc/dice-roller'
  });

  const rollDice = async () => {
    if (numberOfDice < 1 || numberOfDice > 20) {
      toast.error('Number of dice must be between 1 and 20');
      return;
    }
    if (diceSides < 2 || diceSides > 100) {
      toast.error('Dice sides must be between 2 and 100');
      return;
    }

    setIsRolling(true);
    
    // Simulate rolling animation
    const rollDuration = 1000;
    const animationFrames = 20;
    const frameDelay = rollDuration / animationFrames;
    
    for (let i = 0; i < animationFrames; i++) {
      setTimeout(() => {
        const tempRoll = Array.from({ length: numberOfDice }, () => 
          Math.floor(Math.random() * diceSides) + 1
        );
        setCurrentRoll(tempRoll);
      }, i * frameDelay);
    }

    // Final roll
    setTimeout(() => {
      const finalRoll = Array.from({ length: numberOfDice }, () => 
        Math.floor(Math.random() * diceSides) + 1
      );
      setCurrentRoll(finalRoll);
      setIsRolling(false);

      // Add to history
      const rollResult: DiceRoll = {
        dice: finalRoll,
        total: finalRoll.reduce((sum, die) => sum + die, 0) + modifier,
        timestamp: new Date().toLocaleTimeString()
      };
      setRollHistory(prev => [rollResult, ...prev.slice(0, 19)]);

      toast.success(`Rolled ${finalRoll.join(', ')}${modifier !== 0 ? ` + ${modifier}` : ''} = ${rollResult.total}`);
    }, rollDuration);
  };

  const clearHistory = () => {
    setRollHistory([]);
    toast.success('History cleared');
  };

  const getDiceIcon = (value: number): string => {
    const diceUnicode = ['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'];
    return diceSides === 6 && value <= 6 ? diceUnicode[value - 1] : value.toString();
  };

  const presetDice = [
    { name: 'D4', sides: 4, common: true },
    { name: 'D6', sides: 6, common: true },
    { name: 'D8', sides: 8, common: true },
    { name: 'D10', sides: 10, common: true },
    { name: 'D12', sides: 12, common: true },
    { name: 'D20', sides: 20, common: true },
    { name: 'D100', sides: 100, common: true },
    { name: 'Coin', sides: 2, common: false },
    { name: 'D3', sides: 3, common: false }
  ];

  const quickRoll = (sides: number, count: number = 1) => {
    setDiceSides(sides);
    setNumberOfDice(count);
    setTimeout(() => rollDice(), 100);
  };

  const getTotal = (): number => {
    if (currentRoll.length === 0) return 0;
    return currentRoll.reduce((sum, die) => sum + die, 0) + modifier;
  };

  const getDiceColor = (value: number): string => {
    const colors = [
      'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 
      'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-orange-500'
    ];
    return colors[(value - 1) % colors.length];
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <Dices className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            Dice Roller
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Roll virtual dice with customizable sides and quantities. Perfect for board games, 
            D&D campaigns, and any tabletop gaming needs.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Dice Roller */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Roll the Dice</h2>
              
              {/* Dice Configuration */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Dice
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setNumberOfDice(Math.max(1, numberOfDice - 1))}
                      className="p-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                      disabled={numberOfDice <= 1}
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <input
                      type="number"
                      value={numberOfDice}
                      onChange={(e) => setNumberOfDice(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                      min="1"
                      max="20"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent text-center"
                    />
                    <button
                      onClick={() => setNumberOfDice(Math.min(20, numberOfDice + 1))}
                      className="p-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                      disabled={numberOfDice >= 20}
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dice Sides
                  </label>
                  <input
                    type="number"
                    value={diceSides}
                    onChange={(e) => setDiceSides(Math.max(2, Math.min(100, parseInt(e.target.value) || 6)))}
                    min="2"
                    max="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent text-center"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Modifier
                  </label>
                  <input
                    type="number"
                    value={modifier}
                    onChange={(e) => setModifier(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent text-center"
                    placeholder="±0"
                  />
                </div>
              </div>

              {/* Roll Button */}
              <button
                onClick={rollDice}
                disabled={isRolling}
                className="w-full mb-6 px-6 py-4 bg-gradient-to-r from-[#8438ff] to-[#7228ee] text-white rounded-lg hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Play className="h-5 w-5" />
                {isRolling ? 'Rolling...' : `Roll ${numberOfDice}d${diceSides}${modifier !== 0 ? `${modifier >= 0 ? '+' : ''}${modifier}` : ''}`}
              </button>

              {/* Dice Display */}
              {currentRoll.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mb-6"
                >
                  <div className="text-center mb-4">
                    <div className="text-3xl font-bold text-gray-900 mb-2">
                      Total: {getTotal()}
                    </div>
                    {modifier !== 0 && (
                      <div className="text-sm text-gray-600">
                        ({currentRoll.reduce((sum, die) => sum + die, 0)} {modifier >= 0 ? '+' : ''} {modifier})
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap justify-center gap-3">
                    <AnimatePresence>
                      {currentRoll.map((value, index) => (
                        <motion.div
                          key={index}
                          initial={{ rotateX: 0 }}
                          animate={{ rotateX: isRolling ? 360 : 0 }}
                          transition={{ duration: 0.6, ease: "easeInOut" }}
                          className={`w-16 h-16 ${getDiceColor(value)} text-white rounded-lg flex items-center justify-center text-2xl font-bold shadow-lg`}
                        >
                          {diceSides === 6 && value <= 6 ? getDiceIcon(value) : value}
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </motion.div>
              )}

              {/* Preset Dice */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Quick Roll Presets</h3>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {presetDice.map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => quickRoll(preset.sides)}
                      className={`p-3 text-sm rounded-lg border transition-colors ${
                        preset.common 
                          ? 'bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700'
                          : 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-700'
                      }`}
                    >
                      <div className="font-medium">{preset.name}</div>
                      {preset.sides === 2 && <div className="text-xs">Heads/Tails</div>}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Roll History */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Roll History</h3>
                {rollHistory.length > 0 && (
                  <button
                    onClick={clearHistory}
                    className="p-1 text-gray-500 hover:text-red-500 transition-colors"
                    title="Clear history"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {rollHistory.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No rolls yet</p>
                ) : (
                  rollHistory.map((roll, index) => (
                    <div
                      key={index}
                      className="p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-semibold text-gray-900">Total: {roll.total}</span>
                        <span className="text-xs text-gray-500">{roll.timestamp}</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        Dice: {roll.dice.join(', ')}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>

            {/* Statistics */}
            {rollHistory.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4"
              >
                <h3 className="font-semibold text-gray-900 mb-3">Statistics</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Rolls:</span>
                    <span className="font-medium">{rollHistory.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Highest Total:</span>
                    <span className="font-medium">{Math.max(...rollHistory.map(r => r.total))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Lowest Total:</span>
                    <span className="font-medium">{Math.min(...rollHistory.map(r => r.total))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Average:</span>
                    <span className="font-medium">
                      {(rollHistory.reduce((sum, r) => sum + r.total, 0) / rollHistory.length).toFixed(1)}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Gaming Tips */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4"
            >
              <h3 className="font-semibold text-gray-900 mb-3">Gaming Tips</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div>• D20 is commonly used for skill checks in D&D</div>
                <div>• D6 pairs are standard for many board games</div>
                <div>• Use modifiers for character bonuses</div>
                <div>• D100 creates percentile rolls (0-99)</div>
                <div>• Multiple dice create bell curve distributions</div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8 sm:mt-12"
        />
      </div>
    </div>
  );
};

export default DiceRoller;
